import { useState } from 'react'
import { BarChart3, ChevronDown, ChevronRight, Music2, Play, SlidersHorizontal } from 'lucide-react'
import { EXERCISE_COPY } from '../domain/music'
import { createScaleContext, scaleDisplayName } from '../domain/pitchContext'
import type { ExerciseKind, Preferences, ProgressState, TrainingMode } from '../domain/types'
import { PitchRail } from './PitchRail'

type HomeProps = {
  progress: ProgressState
  onStart: (kind: ExerciseKind, mode: TrainingMode) => void
  onOpenProgress: () => void
  onChangePreferences: (preferences: Preferences) => void
}

type ExerciseGroup = {
  label: string
  items: ExerciseKind[]
}

const exerciseGroups: ExerciseGroup[] = [
  { label: 'Absolute Pitch', items: ['notes', 'chord-roots', 'chords', 'absolute-inversions'] },
  { label: 'Relative Pitch', items: ['intervals', 'scales', 'inversions', 'progressions'] },
]

const exerciseGlyph: Record<ExerciseKind, string> = {
  notes: 'C',
  'chord-roots': 'Root',
  intervals: 'M3',
  scales: 'Dorian',
  chords: 'C–E–G',
  'absolute-inversions': 'C/E',
  inversions: '6',
  progressions: 'I–IV–V',
}

const exerciseContent: Record<ExerciseKind, string[]> = {
  notes: ['Pitch-class identification', 'Multiple octave ranges', 'Single-note playback'],
  'chord-roots': ['Absolute root-pitch identification', 'Root-position and inverted voicings', 'Difficulty-based chord vocabulary'],
  intervals: ['Melodic and harmonic intervals', 'Unison through octave', 'Difficulty-based vocabulary'],
  scales: ['Diatonic modes', 'Classical and minor scales', 'Pentatonic and symmetric scales'],
  chords: ['Root pitch + chord quality', '50% credit for each answer part', 'Triads and seventh chords by difficulty'],
  'absolute-inversions': ['Root, quality, and inversion', 'Component-level partial credit', 'Triad and seventh-chord voicings'],
  inversions: ['Root position', 'First and second inversion', 'Bass-note recognition'],
  progressions: ['Classical cadences', 'Jazz harmony', 'Popular and rock progressions'],
}

function groupFor(kind: ExerciseKind) {
  return exerciseGroups.find((group) => group.items.includes(kind))?.label ?? 'Ear Training'
}

export function Home({ progress, onStart, onOpenProgress, onChangePreferences }: HomeProps) {
  const [selected, setSelected] = useState<ExerciseKind>('notes')
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    'Absolute Pitch': true,
    'Relative Pitch': false,
  })
  const copy = EXERCISE_COPY[selected]
  const rounds = progress.rounds.filter((round) => round.kind === selected)
  const correct = rounds.reduce((sum, round) => sum + round.correct, 0)
  const answers = rounds.reduce((sum, round) => sum + round.total, 0)
  const accuracy = answers ? Math.round((correct / answers) * 100) : null
  const absoluteKinds: ExerciseKind[] = ['notes', 'chord-roots', 'chords', 'absolute-inversions']
  const isAbsolutePitch = absoluteKinds.includes(selected)
  const pitchContext = createScaleContext(progress.preferences.absolutePitchTonic, progress.preferences.absolutePitchScaleId)

  const menuItem = (kind: ExerciseKind) => (
    <button
      className={selected === kind ? 'exercise-menu-item nested selected' : 'exercise-menu-item nested'}
      type="button"
      key={kind}
      onClick={() => setSelected(kind)}
      aria-current={selected === kind ? 'page' : undefined}
    >
      <span className="menu-glyph" aria-hidden="true">{exerciseGlyph[kind]}</span>
      <span>{EXERCISE_COPY[kind].title}</span>
      <ChevronRight aria-hidden="true" />
    </button>
  )

  return (
    <main id="main-content" className="section-shell training-home">
      <header className="training-page-heading">
        <div>
          <h1>Ear Training</h1>
          <p>Choose an exercise, then select Practice or Scored Test.</p>
        </div>
        <button className="button button-quiet" type="button" onClick={onOpenProgress}>
          <BarChart3 aria-hidden="true" /> View Progress
        </button>
      </header>

      <div className="training-browser">
        <nav className="exercise-menu" aria-label="Ear training exercises">
          {exerciseGroups.map((group) => (
            <section className="exercise-menu-group" key={group.label} aria-labelledby={`menu-${group.label.toLowerCase().replaceAll(' ', '-')}`}>
              <h2 id={`menu-${group.label.toLowerCase().replaceAll(' ', '-')}`}>
                <button
                  className="exercise-group-toggle"
                  type="button"
                  aria-expanded={openGroups[group.label]}
                  aria-controls={`exercise-group-${group.label.toLowerCase().replaceAll(' ', '-')}`}
                  onClick={() => setOpenGroups((current) => ({ ...current, [group.label]: !current[group.label] }))}
                >
                  <span>{group.label}</span>
                  <ChevronDown aria-hidden="true" />
                </button>
              </h2>
              {openGroups[group.label] && (
                <div id={`exercise-group-${group.label.toLowerCase().replaceAll(' ', '-')}`} className="exercise-group-items">
                  {group.items.map((kind) => menuItem(kind))}
                </div>
              )}
            </section>
          ))}
        </nav>

        <section className="exercise-detail" aria-live="polite">
          <div className="exercise-detail-heading">
            <p>{groupFor(selected)}</p>
            <h2>{copy.title}</h2>
            <span>{copy.description}</span>
          </div>

          {isAbsolutePitch && progress.preferences.showPitchRail && (
            <section className="pitch-context-panel" aria-labelledby="pitch-context-title">
              <div className="pitch-context-heading">
                <div>
                  <span>Pitch context</span>
                  <h3 id="pitch-context-title">
                    {progress.preferences.absolutePitchSource === 'scale'
                      ? `${pitchContext.tonicName} ${scaleDisplayName(pitchContext.scale)}`
                      : 'All 12 pitch classes'}
                  </h3>
                </div>
                <strong>{progress.preferences.absolutePitchSource === 'scale' ? 'Scale-aware questions' : 'Fully random'}</strong>
              </div>
              <PitchRail
                value={progress.preferences.absolutePitchSource === 'scale' ? progress.preferences.absolutePitchTonic : null}
                disabled={progress.preferences.absolutePitchSource !== 'scale'}
                onChange={(pitchClass) => onChangePreferences({ ...progress.preferences, absolutePitchTonic: pitchClass })}
              />
              <p>{progress.preferences.absolutePitchSource === 'scale'
                ? 'The selected tonic sets the scale used for notes and diatonic chords. No reference tone is played.'
                : 'Random mode uses every pitch class. Switch to “Within a scale” under Options to select a tonic.'}</p>
            </section>
          )}

          <div className="exercise-detail-grid">
            <section aria-labelledby="exercise-content-title">
              <h3 id="exercise-content-title">Exercise content</h3>
              <ul className="content-list">
                {exerciseContent[selected].map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
            <section aria-labelledby="exercise-settings-title">
              <h3 id="exercise-settings-title">Current options</h3>
              <dl className="option-summary">
                <div><dt>Difficulty</dt><dd>{progress.preferences.difficulty}</dd></div>
                <div><dt>Questions</dt><dd>{progress.preferences.roundLength}</dd></div>
                <div><dt>Instrument</dt><dd>{progress.preferences.instrument.replace('-', ' ')}</dd></div>
                {(['notes', 'chord-roots', 'chords', 'absolute-inversions'] as ExerciseKind[]).includes(selected) && <div><dt>Pitch range</dt><dd>{progress.preferences.octaveRange} octave{progress.preferences.octaveRange === 1 ? '' : 's'}</dd></div>}
                {(['notes', 'chord-roots', 'chords', 'absolute-inversions'] as ExerciseKind[]).includes(selected) && <div><dt>{selected === 'notes' ? 'Answer input' : 'Root input'}</dt><dd>{progress.preferences.answerInput === 'keyboard' ? 'piano keyboard' : 'note labels'}</dd></div>}
                {isAbsolutePitch && <div><dt>Question source</dt><dd>{progress.preferences.absolutePitchSource === 'scale' ? `${pitchContext.tonicName} ${scaleDisplayName(pitchContext.scale)}` : 'fully random'}</dd></div>}
                {selected === 'scales' && <div><dt>Curated scales</dt><dd>{progress.preferences.selectedScaleIds.length}</dd></div>}
                {selected === 'progressions' && <div><dt>Curated progressions</dt><dd>{progress.preferences.selectedProgressionIds.length}</dd></div>}
              </dl>
            </section>
          </div>

          <div className="exercise-history" aria-label={`${copy.title} history`}>
            <span><strong>{rounds.length}</strong> completed rounds</span>
            <span><strong>{accuracy === null ? '—' : `${accuracy}%`}</strong> accuracy</span>
          </div>

          <div className="training-actions">
            <button className="button button-primary button-large" type="button" onClick={() => onStart(selected, 'practice')}>
              <Play aria-hidden="true" /> Practice
            </button>
            <button className="button button-quiet button-large" type="button" onClick={() => onStart(selected, 'scored')}>
              <Music2 aria-hidden="true" /> Scored Test
            </button>
          </div>

          <p className="mode-note"><SlidersHorizontal aria-hidden="true" /> Change difficulty, playback, instrument, and round length under Options.</p>
        </section>
      </div>
    </main>
  )
}
